import {
  CarouselBeta,
  CarouselSlideBeta,
  ChipGroup,
  H3,
  SelectableChip,
  Skeleton,
} from '@zooplus/ui-components';
import { useTranslation } from '../../hooks/use-translations';
import React, { ReactNode, useEffect, useState } from 'react';
import { Product } from '../common/product/Product';
import styles from './MostSubscribed.module.scss';
import { Bird, Cat, Dog, Fish, Horse, Rabbit } from '@zooplus/icons';
import { EmblaOptionsType } from 'embla-carousel';
import { ProductInfo } from '../../model/product';
import { CentralHubService } from '../../services/CentralHubService';
import { useSiteContext } from '@zooplus/commons-lang';
import { CartArticleV2, useCartContext } from '@zooplus/cart-provider';

type Category = {
  key: string;
  icon?: ReactNode;
};

export const MostSubscribed = () => {
  const { site, language } = useSiteContext();
  const { t } = useTranslation();
  const { cart } = useCartContext();
  const [categorySelected, setCategorySelected] = useState('all');

  const categories: Category[] = [
    { key: 'all' },
    { key: 'dog', icon: <Dog /> },
    { key: 'cat', icon: <Cat /> },
    { key: 'small', icon: <Rabbit /> },
    { key: 'fish', icon: <Fish /> },
    { key: 'bird', icon: <Bird /> },
    { key: 'horse', icon: <Horse /> },
  ];

  const options: EmblaOptionsType = {
    align: 'center',
    breakpoints: {
      '(min-width: 0px)': {
        align: 'center',
        slidesToScroll: 1,
      },
      '(min-width: 544px)': {
        align: 'start',
        slidesToScroll: 2,
      },
      '(min-width: 768px)': {
        slidesToScroll: 4,
      },
      '(min-width: 1280px)': {
        slidesToScroll: 6,
      },
      '(min-width: 1920px)': {
        slidesToScroll: 7,
      },
    },
    slidesToScroll: 1,
  };

  const [baseProducts, setBaseProducts] = useState<ProductInfo[] | undefined>(undefined);
  const [productsDisplay, setProductsDisplay] = useState<ProductInfo[] | undefined>(undefined);

  useEffect(() => {
    CentralHubService(undefined)
      .getMostSubscribedProducts(site.domain, language)
      .then((response) => {
        if (response) {
          setBaseProducts(
            response.products.map((product) => {
              return {
                ...product,
                disabled:
                  cart?.articles?.some(
                    (article: CartArticleV2) => article.id === product.articleShopId,
                  ) || false,
              };
            }),
          );
        }
      });
  }, [cart]);

  useEffect(() => {
    if (baseProducts)
      setProductsDisplay(
        baseProducts.filter(
          (product) =>
            product.category.toLowerCase() === categorySelected || categorySelected === 'all',
        ),
      );
  }, [baseProducts, categorySelected]);

  return (
    <section>
      <H3 className={styles.title}>{t('most-subscribed.title')}</H3>
      <ChipGroup className="pp-mb-m">
        {categories
          .filter(
            (category) =>
              category.key === 'all' ||
              baseProducts?.some((product) => product.category.toLowerCase() === category.key),
          )
          .map((category) => (
            <SelectableChip
              key={`chip-category-${category.key}`}
              inputType="radio"
              inputName="most-suscribed-category"
              label={t(`most-subscribed.categories.${category.key}`)}
              inputId={`most-subscribed-category-${category.key}`}
              onClick={() => setCategorySelected(category.key)}
              icon={category.icon}
              selected={category.key === 'all'}
            />
          ))}
      </ChipGroup>
      {productsDisplay ? (
        <CarouselBeta
          emblaOptions={options}
          className={styles.carousel}
          id="most-subscribed-products"
          gap={'12px'}
          nextButtonAriaLabel="next"
          previousButtonAriaLabel="previous">
          {productsDisplay.map((product) => (
            <CarouselSlideBeta
              className={styles.carouselItem}
              key={`product-${product.articleShopId}`}>
              <Product product={product} />
            </CarouselSlideBeta>
          ))}
        </CarouselBeta>
      ) : (
        <Skeleton className={styles.skeleton} height={'415'} />
      )}
    </section>
  );
};
